import React from 'react';
import { Switch, Redirect } from 'react-router-dom';

import AuthTransitions from 'modules/auth-transitions';
import PaymentStatus from 'screens/payment-status';
import HallOfFame from 'screens/hall-of-fame';
import Curriculum from 'screens/curriculum';
import CurriculumGraph from 'screens/curriculum-graph';
import CurriculumAnalytics from 'screens/curriculum-analytics';
import PricingManagement from 'screens/pricing-management';
import Eval from 'screens/eval';
import EvalSpecialistRoster from 'screens/eval/specialist-roster';
import Evals from 'screens/eval/evals';
import EvalAvailability from 'screens/eval/availability';
import EvalCapacity from 'screens/eval/capacity';
import EvalFailedMappings from 'screens/eval/failed-mappings';
import EvalPrepTasks from 'screens/eval/prep-tasks';
import EvalPrepCompliance from 'screens/eval/prep-compliance';
import EvalParentFeedback from 'screens/eval/parent-feedback';
import StudentPayment from 'screens/student-payment';
import PIIAssignmentContainer from 'screens/pii-info';
import DemoIncentive from 'screens/demo-incentive';
import TeacherAnalytics from 'screens/teacher-analytics';
import StudentAnalyticsAggregate from 'screens/student-analytics';
import SingleTeacherAnalytics from 'screens/single-teacher-analytics';
import BatchAssignmentContainer from 'screens/batch-assignment';
import StudentForm from 'screens/batch-assignment/student-form';
import StudentPII from 'screens/pii-info/student-pii';
import ParentPII from 'screens/pii-info/parent-pii';
import StudentFormParent from 'screens/batch-assignment/student-form-parent';
import ActivityAnalytics from 'screens/activity-aggregation';
import ChapterAnalytics from 'screens/chapter-aggregation';
import ProgramAnalytics from 'screens/program-activity';
import TeacherForm from 'screens/batch-assignment/teacher-form';
import TeacherDetailView from 'screens/batch-assignment/teacher-detail';
import TeacherClassroom from 'screens/teacher-classroom';
import DemoClass from 'screens/demo-class';
import UserVibeContainer from 'screens/user-vibe';
import ClassroomDashboard from 'screens/trial-dashboard';
import AnnotatePage from 'screens/trial-dashboard/annotate-page';
import ClassroomViewPage from 'screens/trial-dashboard/classroom-view-page';
import AddStudentForm from './screens/add-parent/add-student-form';
import AddParentStudentContainer from './screens/add-parent-student';
import AddParentStudentForm from './screens/add-parent-student/add-student-form';
import AVCallContainer from './screens/av-call';
import TeacherStudentList from './screens/batch-assignment/teacher-form/teacher-students';
import StudentList from './screens/students-list';
import UserDetails from './screens/student-details';
import TeacherMapping from './screens/batch-assignment/teacher-mapping';
import Login from 'screens/login';
import Home from 'screens/home';
import PtmReport from './screens/ptm-report';
import Applicants from './screens/batch-assignment/applicants';
import ApplicantForm from './screens/batch-assignment/applicant-form';
import ReferralCampaignScreen from './screens/referral-campaign';
import AutoMapping from './screens/auto-mapping';
import WorkbookManagement from 'screens/workbook-management';
import LearnosityItemUpdated from 'screens/learnosity';
import ChatbotAdmin from 'screens/chatbot';
import StudentTransfer from './screens/student-transfer';
import FestReport from './screens/fest-report';
import RegionPayout from './screens/region-payout';

import ModalProvider from 'modules/modal';
import Notification from 'modules/notification';
import { PubnubProvider } from './contexts/pubnub-context';
import NotificationToastContainer from 'modules/notification-toast';

import PublicRoute from './routes/public';
import PrivateRoute from './routes/private';
import PaymentRoute from './routes/payment';
import {
  AC_LED_MAPPING_ROUTE,
  AC_LED_MAPPING_ROUTE_V2,
} from './constants/ac-led-mapping';
import AcLedMapping from 'screens/ac-led-mapping';
import AcLedMappingV2 from './screens/ac-led-mapping/v2';
import STTeacherDetails from './screens/student-transfer-teacher-details';
import CSUtils from './screens/cs-utils';
import CsmDetails from './screens/csm-details';
import MaintenanceScreen from 'modules/maintenance';
import AdmissionTool from './screens/admission-tool';
import AVReviewDashboard from './screens/av-review-dashboard';
import AVReviewDetail from './screens/av-review-detail';
import WebsiteMetrics from './screens/website-metrics';
import AVReviewSettings from './screens/av-review-settings';
import SupportAgent from './screens/support-agent';
import SupportQueue from './screens/support-queue';
import InternalTickets from './screens/internal-tickets';

export default class AppRouter extends React.Component {
  render() {
    return (
      <React.Fragment>
        <AuthTransitions />
        <ModalProvider />
        <Notification />
        <NotificationToastContainer />
        <MaintenanceScreen />
        <PublicRoute path="/login/" component={Login} />
        <PaymentRoute path="/payment-successful/" component={PaymentStatus} />
        <PaymentRoute path="/payment-cancelled/" component={PaymentStatus} />
        <Switch>
          <PrivateRoute exact path="/" component={Home} access={true} />
          <PrivateRoute exact path="/students" component={StudentList} />
          {/* access={true} is deliberate, as on Home and /website-metrics: isStaff is
              derived from the ABSENCE of a user_id cookie (reducers/auth.js), so staff who
              have impersonated a student would be locked out. The real gate is server-side —
              every /hof/ endpoint requires is_staff, so a non-staff session gets a 403. */}
          <PrivateRoute exact path="/hall-of-fame" component={HallOfFame} access={true} />
          <PrivateRoute
            exact
            path="/students/:id"
            component={UserDetails}
            access={true}
          />
          <PrivateRoute exact path="/ptm-report/:id" component={PtmReport} />
          <PrivateRoute path="/curriculum/" component={Curriculum} />
          <PrivateRoute path="/curriculum-graph/" component={CurriculumGraph} />
          <PrivateRoute path="/teacher-analytics/" component={TeacherAnalytics} />
          <PrivateRoute path="/demo/:studentClassroomId" component={DemoClass} />
          <PrivateRoute exact path="/users/" component={BatchAssignmentContainer} />
          <PrivateRoute exact path="/pricing-management/" component={PricingManagement} />
          <PrivateRoute exact path="/eval/" component={Eval} />
          <PrivateRoute
            exact
            path="/eval/specialist-roster/"
            component={EvalSpecialistRoster}
          />
          <PrivateRoute exact path="/eval/evals/" component={Evals} />
          <PrivateRoute exact path="/eval/capacity/" component={EvalCapacity} />
          <PrivateRoute exact path="/eval/availability/" component={EvalAvailability} />
          <PrivateRoute
            exact
            path="/eval/failed-mappings/"
            component={EvalFailedMappings}
          />
          <PrivateRoute exact path="/eval/prep-tasks/" component={EvalPrepTasks} />
          <PrivateRoute
            exact
            path="/eval/parent-feedback/"
            component={EvalParentFeedback}
          />
          <PrivateRoute
            exact
            path="/eval/prep-compliance/"
            component={EvalPrepCompliance}
          />
          <PrivateRoute
            exact
            path="/classroom-dashboard/"
            component={ClassroomDashboard}
          />
          <Redirect from="/trial-dashboard" to="/classroom-dashboard" />
          <PrivateRoute
            exact
            path="/annotate/:teacherClassroomId"
            component={AnnotatePage}
          />
          <PrivateRoute
            exact
            path="/classroom-view/:teacherClassroomId"
            component={ClassroomViewPage}
          />
          <PrivateRoute exact path={AC_LED_MAPPING_ROUTE} component={AcLedMapping} />
          <PrivateRoute exact path={AC_LED_MAPPING_ROUTE_V2} component={AcLedMappingV2} />
          <PrivateRoute
            exact
            path="/workbook-management/"
            component={WorkbookManagement}
          />
          <PrivateRoute
            exact
            path="/learnosity-item-updated/"
            component={LearnosityItemUpdated}
          />
          <PrivateRoute exact path="/student-transfer/" component={StudentTransfer} />
          <PrivateRoute
            exact
            path="/student-transfer/:teacherId"
            component={STTeacherDetails}
          />
          <PrivateRoute exact path="/cs-utils/" component={CSUtils} />
          <PrivateRoute exact path="/student-payments/" component={StudentPayment} />
          <PrivateRoute
            path="/activity-analytics/:activityId/"
            component={ActivityAnalytics}
          />
          <PrivateRoute
            path="/chapter-analytics/:chapterCode/:chapterVersion/"
            component={ChapterAnalytics}
          />
          <PrivateRoute
            path="/curriculum-analytics/"
            exact
            component={CurriculumAnalytics}
          />
          <PrivateRoute
            path="/curriculum-analytics/:activeTab/"
            exact
            component={CurriculumAnalytics}
          />
          <PrivateRoute
            path="/curriculum-analytics/:activeTab/:programId"
            exact
            component={CurriculumAnalytics}
          />
          <PrivateRoute
            path="/pricing-management/:activeTab/"
            component={PricingManagement}
          />
          <PrivateRoute
            path="/workbook-management/:activeTab/"
            component={WorkbookManagement}
          />
          <PrivateRoute path="/student-payments/" component={StudentPayment} />
          <PrivateRoute exact path="/pii-info/" component={PIIAssignmentContainer} />
          <PrivateRoute path="/demo-incentive/" component={DemoIncentive} />
          <PrivateRoute
            path="/program-analytics/:programCode/"
            component={ProgramAnalytics}
          />
          <PrivateRoute
            exact
            path="/pii-info/:activeTab/"
            component={PIIAssignmentContainer}
          />
          <PrivateRoute
            path="/student-analytics/"
            component={StudentAnalyticsAggregate}
          />
          <PrivateRoute path="/teacher/:teacherId" component={SingleTeacherAnalytics} />
          <PrivateRoute
            exact
            path="/users/:activeTab/"
            component={BatchAssignmentContainer}
          />
          <PrivateRoute exact path="/teacher-form/:teacherId" component={TeacherForm} />
          <PrivateRoute
            exact
            path="/users/teachers/:teacherId"
            component={TeacherDetailView}
          />
          <PrivateRoute exact path="/users/csm/:csmId" component={CsmDetails} />
          <PrivateRoute
            exact
            path="/users/teacher-classroom/:teacherClassroomId"
            component={TeacherClassroom}
            access={true}
          />
          <PrivateRoute path="/student-form/:studentId/" component={StudentForm} />
          <PrivateRoute path="/student-pii/:studentId/" component={StudentPII} />
          <PrivateRoute path="/parent-pii/:parentId/" component={ParentPII} />
          <PrivateRoute path="/teacher-mapping/:studentId/" component={TeacherMapping} />
          <PrivateRoute
            path="/student-form-parent/:studentId/:demo"
            component={StudentFormParent}
          />
          <PrivateRoute exact path="/user-vibe/" component={UserVibeContainer} />
          <PrivateRoute
            exact
            path="/teacher-students/:teacherId"
            component={TeacherStudentList}
          />
          <PrivateRoute
            exact
            path="/user-vibe/:activeTab/"
            component={UserVibeContainer}
          />
          <PrivateRoute exact path="/add-parent">
            <Redirect to="/add-parent-student" />
          </PrivateRoute>
          <PrivateRoute path="/parent/:parentId/student/" component={AddStudentForm} />
          <PrivateRoute
            exact
            path="/add-parent-student"
            component={AddParentStudentContainer}
          />
          <PrivateRoute
            path="/parent-student/:parentId/student/"
            component={AddParentStudentForm}
          />
          <PrivateRoute exact path="/users/teacher-applicants/" component={Applicants} />
          <PrivateRoute
            exact
            path="/teacher-applicants/:applicantId"
            component={ApplicantForm}
          />
          <PrivateRoute path="/referral-campaign" component={ReferralCampaignScreen} />
          <PrivateRoute
            exact
            path="/av-reviews"
            component={AVReviewDashboard}
            access={true}
          />
          <PrivateRoute
            exact
            path="/av-reviews/:reviewId"
            component={AVReviewDetail}
            access={true}
          />
          <PrivateRoute
            exact
            path="/av-reviews-settings"
            component={AVReviewSettings}
            access={true}
          />
          {/* access={true} is deliberate: isStaff is derived from the ABSENCE of
              a user_id cookie (reducers/auth.js), so staff who have impersonated a
              student/parent carry user_id and would be locked out. The real gate
              is the staff-only login, same as Home and the AV dashboard. */}
          <PrivateRoute
            exact
            path="/website-metrics"
            component={WebsiteMetrics}
            access={true}
          />
          <PrivateRoute
            exact
            path="/website-metrics/:tab"
            component={WebsiteMetrics}
            access={true}
          />
          <PrivateRoute exact path="/auto-mapping/" component={AutoMapping} />
          <PrivateRoute exact path="/chatbot/" component={ChatbotAdmin} />
          <PrivateRoute exact path="/chatbot/:tab" component={ChatbotAdmin} />
          <PrivateRoute exact path="/support-agent" component={SupportAgent} />
          <PrivateRoute exact path="/support-queue" component={SupportQueue} />
          <PrivateRoute exact path="/internal-tickets" component={InternalTickets} />
          <PrivateRoute exact path="/cuemath-fest-report/" component={FestReport} />
          <PrivateRoute exact path="/region-payout/" component={RegionPayout} />
          <PrivateRoute
            exact
            path="/admission-tool/:prospectId/"
            component={AdmissionTool}
          />
          <PubnubProvider>
            <PrivateRoute
              exact
              path="/student-demo-room/:teacherClassroomId"
              component={AVCallContainer}
            />
          </PubnubProvider>
        </Switch>
      </React.Fragment>
    );
  }
}
